import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { CompanyContact } from '../../models/business/CompanyContact'
import { companyContactSchema } from '../../models/schema'
import {
  createCompanyContact as createCompanyContactAPI,
  deleteCompanyContact as deleteCompanyContactAPI,
  fetchCompanyContactById,
  fetchCompanyContacts,
  updateCompanyContact as updateCompanyContactAPI,
} from '../../services/business/CompanyContactService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<CompanyContact> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'companyContact',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getCompanyContactsSuccess,
  setQuery: setCompanyContactQuery,
  reset: resetCompanyContactState,
  resetQueryAndIds: resetCompanyContactQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: CompanyContact }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectCompanyContacts = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCompanyContact = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectCompanyContactById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectCompanyContactsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedCompanyContact =
  (companyContact: CompanyContact | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<CompanyContact>(companyContact)
      )
    )

export const getCompanyContacts = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchCompanyContacts(params),
    [companyContactSchema],
    slice.actions
  )

export const createCompanyContact = (companyContact: CompanyContact) =>
  SBAPI.CreateDispatched(
    createCompanyContactAPI(companyContact),
    companyContactSchema,
    slice.actions
  )

export const getCompanyContactWithId = (id: number) =>
  SBAPI.FetchDispatched<CompanyContact>(
    fetchCompanyContactById(id),
    companyContactSchema,
    slice.actions
  )

export const updateCompanyContact = (companyContact: CompanyContact) =>
  SBAPI.UpdateDispatched<CompanyContact>(
    updateCompanyContactAPI(companyContact),
    companyContact,
    slice.actions
  )

export const deleteCompanyContact = (companyContact: CompanyContact) =>
  SBAPI.DestroyDispatched<CompanyContact>(
    deleteCompanyContactAPI(companyContact),
    companyContact,
    slice.actions
  )
